import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Megaphone, Wrench, ShieldAlert, Receipt,
  CalendarDays, FileText, Users, Vote, Radio, LogOut, Gavel, Sparkles, CreditCard,
  BarChart3, MessageSquare, Landmark, GraduationCap, Home,
} from "lucide-react";
import CommunityIQLogo from "@/components/CommunityIQLogo";
import AISaintWordmark from "@/components/AISaintWordmark";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "board", "owner"] },
  { to: "/my-balance", label: "My Balance", icon: Home, roles: ["owner"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["admin", "board", "owner"] },
  { to: "/work-orders", label: "Work Orders", icon: Wrench, roles: ["admin", "board", "owner"] },
  { to: "/violations", label: "Violations", icon: ShieldAlert, roles: ["admin", "board", "owner"] },
  { to: "/dues", label: "Dues", icon: Receipt, roles: ["admin", "board", "owner"] },
  { to: "/meetings", label: "Meetings", icon: CalendarDays, roles: ["admin", "board", "owner"] },
  { to: "/documents", label: "Documents", icon: FileText, roles: ["admin", "board", "owner"] },
  { to: "/inquiries", label: "Owner Requests", icon: MessageSquare, roles: ["admin", "board"] },
  { to: "/inquiries", label: "My Inquiries", icon: MessageSquare, roles: ["owner"] },
  { to: "/polls", label: "Polls", icon: Vote, roles: ["admin", "board", "owner"] },
  { to: "/live-vote", label: "Live Vote", icon: Radio, roles: ["admin", "board", "owner"] },
  { to: "/directory", label: "Directory", icon: Users, roles: ["admin", "board", "owner"] },
  { to: "/community", label: "Community Hub", icon: MessageSquare, roles: ["admin", "board", "owner"] },
  { to: "/financial-reports", label: "Financial Reports", icon: BarChart3, roles: ["admin", "board"] },
  { to: "/board-ops", label: "Board Ops", icon: Landmark, roles: ["admin", "board", "owner"] },
  { to: "/legal-suite", label: "Legal Suite", icon: Gavel, roles: ["admin", "board"] },
  { to: "/ai-saint", label: "AI Saint", icon: Sparkles, roles: ["admin", "board"] },
  { to: "/subscription", label: "Subscription", icon: CreditCard, roles: ["admin", "board"] },
];

const MOBILE_NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/announcements", label: "News", icon: Megaphone },
  { to: "/work-orders", label: "Requests", icon: Wrench },
  { to: "/dues", label: "Dues", icon: Receipt },
  { to: "/community", label: "Community", icon: MessageSquare },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const roleLabel = {
    admin: "Administrator",
    board: "Board Director",
    owner: "Homeowner",
  }[user?.role] || "Member";

  return (
    <div className="min-h-screen bg-background paper-texture">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-screen w-64 flex-col bg-card border-r border-border p-6 z-30">
        <div className="mb-8">
          <CommunityIQLogo className="w-full h-auto" />
          <p className="text-lg text-gold italic text-center mt-3 leading-snug">
            &ldquo;It takes a village.&rdquo;
          </p>
        </div>
        <nav className="flex-1 flex flex-col gap-1" data-testid="sidebar-nav">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground glow-purple"
                    : "text-foreground/70 hover:bg-accent hover:text-gold"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" strokeWidth={2} />
              {n.label === "AI Saint" ? <AISaintWordmark /> : n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-4 pt-4 border-t border-border">
          <div className="mb-3">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{roleLabel}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col items-center gap-1">
          <CommunityIQLogo className="h-10 w-auto" />
          <p className="text-xs text-gold italic leading-none">It takes a village.</p>
        </div>
        <button
          onClick={handleLogout}
          className="text-sm text-muted-foreground flex items-center gap-1"
          data-testid="mobile-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </header>

      {/* Main */}
      <main className="lg:pl-64 pb-24 lg:pb-8">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border grid grid-cols-5 z-30"
        data-testid="mobile-bottom-nav"
      >
        {MOBILE_NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`bottom-nav-${n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-xs ${
                isActive ? "text-gold" : "text-muted-foreground"
              }`
            }
          >
            <n.icon className="w-5 h-5" strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
